// src/crm/ugc/PaymentsTab.tsx
//
// Onglet « Publications et paiements » du programme UGC (spec § 6.3) : captures à valider,
// relevés à venir (« Capture manquante » : valider sans capture ou supprimer), sommes à payer par
// personne (« Facture reçue » ou « Réglé autrement »), règlements en cours (« Payé »), relevés
// validés encore corrigeables et historique des règlements. Les actions sans retour passent par
// une confirmation dans la page. Tant que le montant pour 1 000 vues d'une personne n'est pas fixé,
// ses relevés ne se valident pas (l'API répond 409 `rate_missing`) : la carte le dit et mène à
// l'onglet « Montants à fixer ».

import { useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { formatEuros } from '../../../functions/lib/ugc/rules'
import type { AdminPaymentsView, AdminPublicationItem, PayableItem, UgcPlatform } from '../../lib/ugcTypes'
import { formatDate } from './format'
import {
  useCancelSettlement,
  useCorrectReading,
  useCreateSettlement,
  useDeletePublication,
  useMarkSettlementPaid,
  useRejectScreenshot,
  useUgcPayments,
  useUgcPublications,
  useValidateReading,
} from './hooks'

const PLATFORM_LABELS: Record<UgcPlatform, string> = { tiktok: 'TikTok', instagram: 'Instagram' }
const VIEWS = new Intl.NumberFormat('fr-FR')
const PARIS_DAY = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'Europe/Paris' })

/** Jour de Paris au format AAAA-MM-JJ (valeur par défaut de « Date du paiement »). */
function parisToday(): string {
  return PARIS_DAY.format(new Date())
}

/** Nombre de vues saisi : entier positif ou nul, sinon `null`. */
function parseViews(text: string): number | null {
  if (!/^\d{1,9}$/.test(text.trim())) return null
  return Number(text.trim())
}

function ErrorLine({ error }: { error: Error | null }) {
  if (!error) return null
  return (
    <p role="alert" className="ugc-error">
      {error.message}
    </p>
  )
}

function ViewsField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const id = useId()
  return (
    <div className="ugc-field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        className="ugc-input"
        type="number"
        inputMode="numeric"
        min={0}
        step={1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  )
}

function PublicationHead({ item, titleId }: { item: AdminPublicationItem; titleId: string }) {
  const p = item.publication
  return (
    <header className="ugc-card-head">
      <h3 id={titleId} className="ugc-card-title">
        {item.videoTitle}
      </h3>
      <span className="ugc-muted">
        {item.memberName},{' '}
        <a className="ugc-link" href={p.url} target="_blank" rel="noreferrer">
          {PLATFORM_LABELS[p.platform]}
        </a>
        , publiée le {formatDate(p.publishedOn)}
      </span>
    </header>
  )
}

/** Relevé bloqué : le montant pour 1 000 vues de la personne n'est pas encore fixé. */
function RateMissing({ item }: { item: AdminPublicationItem }) {
  if (item.memberRateCentsPerThousand !== null) return null
  return (
    <div className="ugc-blocked" role="note">
      <strong>Montant pour 1 000 vues à fixer</strong>
      <span>
        Le relevé de {item.memberName || 'cette personne'} attend son montant pour 1 000 vues.{' '}
        <Link className="ugc-link" to="/crm/ugc/montants">
          Montants à fixer
        </Link>
        {item.memberCreatorId ? (
          <>
            {' · '}
            <Link className="ugc-link" to={`/crm/contacts/${encodeURIComponent(item.memberCreatorId)}`}>
              Fiche
            </Link>
          </>
        ) : null}
      </span>
    </div>
  )
}

// ── Captures à valider ───────────────────────────────────────────────────────

function ScreenshotCard({ item }: { item: AdminPublicationItem }) {
  const p = item.publication
  const titleId = useId()
  const commentId = useId()
  const validate = useValidateReading()
  const reject = useRejectScreenshot()
  const [views, setViews] = useState(p.declaredViews === null ? '' : String(p.declaredViews))
  const [asking, setAsking] = useState(false)
  const [comment, setComment] = useState('')
  const parsed = parseViews(views)
  const busy = validate.isPending || reject.isPending
  const rateMissing = item.memberRateCentsPerThousand === null
  const src = `/api/ugc/publications/${encodeURIComponent(p.id)}/screenshot`

  return (
    <article className="ugc-card" aria-labelledby={titleId}>
      <PublicationHead item={item} titleId={titleId} />
      <a href={src} target="_blank" rel="noreferrer" className="ugc-shot-link">
        <img className="ugc-shot" src={src} alt={`Capture des vues de ${item.videoTitle}`} loading="lazy" />
      </a>
      <dl className="ugc-facts">
        <div>
          <dt>Vues déclarées</dt>
          <dd>{p.declaredViews === null ? 'Non indiquées' : VIEWS.format(p.declaredViews)}</dd>
        </div>
        <div>
          <dt>Capture envoyée</dt>
          <dd>{p.screenshotSubmittedAt ? formatDate(p.screenshotSubmittedAt) : 'Non'}</dd>
        </div>
        <div>
          <dt>Relevé prévu (30e jour)</dt>
          <dd>{formatDate(p.readingDueOn)}</dd>
        </div>
      </dl>
      <ViewsField label="Vues retenues" value={views} onChange={setViews} />
      <RateMissing item={item} />
      {!asking && (
        <div className="ugc-actions">
          <button
            type="button"
            className="ugc-btn ugc-btn--go"
            disabled={busy || parsed === null || rateMissing}
            onClick={() => {
              reject.reset()
              if (parsed !== null) validate.mutate({ id: p.id, views: parsed, withoutScreenshot: false })
            }}
          >
            {validate.isPending ? 'Validation…' : 'Valider'}
          </button>
          <button type="button" className="ugc-btn" disabled={busy} onClick={() => setAsking(true)}>
            Demander une autre capture
          </button>
        </div>
      )}
      {asking && (
        <div className="ugc-inline-form">
          <label htmlFor={commentId}>Commentaire pour la personne</label>
          <textarea
            id={commentId}
            className="ugc-textarea"
            rows={3}
            maxLength={1000}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <div className="ugc-actions">
            <button
              type="button"
              className="ugc-btn ugc-btn--strong"
              disabled={busy || comment.trim() === ''}
              onClick={() => {
                validate.reset()
                reject.mutate({ id: p.id, comment: comment.trim() })
              }}
            >
              Envoyer la demande
            </button>
            <button type="button" className="ugc-btn" disabled={busy} onClick={() => setAsking(false)}>
              Annuler
            </button>
          </div>
        </div>
      )}
      <ErrorLine error={validate.error ?? reject.error} />
    </article>
  )
}

// ── Relevés à venir ──────────────────────────────────────────────────────────

function UpcomingCard({ item }: { item: AdminPublicationItem }) {
  const p = item.publication
  const titleId = useId()
  const validate = useValidateReading()
  const remove = useDeletePublication()
  const [step, setStep] = useState<'idle' | 'manual' | 'deleting'>('idle')
  const [views, setViews] = useState('')
  const parsed = parseViews(views)
  const busy = validate.isPending || remove.isPending
  const rateMissing = item.memberRateCentsPerThousand === null

  return (
    <article className="ugc-card" aria-labelledby={titleId}>
      <PublicationHead item={item} titleId={titleId} />
      <dl className="ugc-facts">
        <div>
          <dt>Relevé</dt>
          <dd>{formatDate(p.readingDueOn)}</dd>
        </div>
        <div>
          <dt>En ligne jusqu'au</dt>
          <dd>{formatDate(item.onlineUntil)}</dd>
        </div>
        {p.remindersSent > 0 && (
          <div>
            <dt>Rappels envoyés</dt>
            <dd>{p.remindersSent}</dd>
          </div>
        )}
      </dl>
      {item.missing && <p className="ugc-badge ugc-badge--warn">Capture manquante</p>}
      {item.missing && <RateMissing item={item} />}
      {item.missing && step === 'idle' && (
        <div className="ugc-actions">
          <button type="button" className="ugc-btn ugc-btn--strong" disabled={rateMissing} onClick={() => setStep('manual')}>
            Valider sans capture
          </button>
          <button type="button" className="ugc-btn" onClick={() => setStep('deleting')}>
            Supprimer la publication
          </button>
        </div>
      )}
      {step === 'manual' && (
        <div className="ugc-inline-form">
          <ViewsField label="Vues relevées" value={views} onChange={setViews} />
          <p className="ugc-muted">0 si la vidéo a disparu. Le montant se fige si ses autres publications sont validées.</p>
          <div className="ugc-actions">
            <button
              type="button"
              className="ugc-btn ugc-btn--go"
              disabled={busy || parsed === null || rateMissing}
              onClick={() => {
                if (parsed !== null) validate.mutate({ id: p.id, views: parsed, withoutScreenshot: true })
              }}
            >
              Confirmer le relevé
            </button>
            <button type="button" className="ugc-btn" disabled={busy} onClick={() => setStep('idle')}>
              Annuler
            </button>
          </div>
        </div>
      )}
      {step === 'deleting' && (
        <div className="ugc-inline-form">
          <p className="ugc-confirm">
            La publication {PLATFORM_LABELS[p.platform]} de {item.memberName} sera supprimée. Le montant de la vidéo se fige
            si ses autres publications sont validées.
          </p>
          <div className="ugc-actions">
            <button type="button" className="ugc-btn ugc-btn--strong" disabled={busy} onClick={() => remove.mutate(p.id)}>
              Confirmer la suppression
            </button>
            <button type="button" className="ugc-btn" disabled={busy} onClick={() => setStep('idle')}>
              Annuler
            </button>
          </div>
        </div>
      )}
      <ErrorLine error={validate.error ?? remove.error} />
    </article>
  )
}

// ── Relevés validés, corrigeables tant que le montant n'est pas réglé ───────

const REFERRAL_NOTES: Record<string, string> = {
  reverted: 'Le parrainage que ce montant avait fait gagner repasse en attente.',
  earned: 'Ce montant fait gagner son parrainage.',
  settled: "Attention : le parrainage gagné par ce montant est déjà réglé, il n'a pas bougé.",
}

function ValidatedCard({ item }: { item: AdminPublicationItem }) {
  const p = item.publication
  const titleId = useId()
  const correct = useCorrectReading()
  const [open, setOpen] = useState(false)
  const [views, setViews] = useState(String(p.validatedViews ?? 0))
  const parsed = parseViews(views)
  const note = correct.data ? REFERRAL_NOTES[correct.data.referral] : undefined

  return (
    <article className="ugc-card" aria-labelledby={titleId}>
      <PublicationHead item={item} titleId={titleId} />
      <dl className="ugc-facts">
        <div>
          <dt>Vues retenues</dt>
          <dd>{VIEWS.format(p.validatedViews ?? 0)}</dd>
        </div>
        {item.payout && (
          <div>
            <dt>Montant de la vidéo</dt>
            <dd>
              {formatEuros(item.payout.amountCents)}
              {item.payout.overQuota ? ' (hors quota)' : ''}
            </dd>
          </div>
        )}
      </dl>
      {!open && (
        <div className="ugc-actions">
          <button type="button" className="ugc-btn" onClick={() => setOpen(true)}>
            Corriger le relevé
          </button>
        </div>
      )}
      {open && (
        <div className="ugc-inline-form">
          <ViewsField label="Vues corrigées" value={views} onChange={setViews} />
          <div className="ugc-actions">
            <button
              type="button"
              className="ugc-btn ugc-btn--strong"
              disabled={correct.isPending || parsed === null}
              onClick={() => {
                if (parsed !== null) correct.mutate({ id: p.id, views: parsed }, { onSuccess: () => setOpen(false) })
              }}
            >
              Enregistrer la correction
            </button>
            <button type="button" className="ugc-btn" disabled={correct.isPending} onClick={() => setOpen(false)}>
              Annuler
            </button>
          </div>
        </div>
      )}
      {note && (
        <p role="status" className="ugc-notice">
          {note}
        </p>
      )}
      <ErrorLine error={correct.error} />
    </article>
  )
}

// ── À payer ──────────────────────────────────────────────────────────────────

type DueGroup = AdminPaymentsView['due'][number]

function DueGroupCard({ group }: { group: DueGroup }) {
  const create = useCreateSettlement()
  const noteId = useId()
  const [unchecked, setUnchecked] = useState<ReadonlySet<string>>(new Set())
  const [arranging, setArranging] = useState(false)
  const [note, setNote] = useState('')
  const key = (item: PayableItem) => `${item.kind}:${item.id}`
  const checked = group.items.filter((item) => !unchecked.has(key(item)))
  const total = checked.reduce((sum, item) => sum + item.amountCents, 0)

  function toggle(item: PayableItem) {
    setUnchecked((prev) => {
      const next = new Set(prev)
      if (next.has(key(item))) next.delete(key(item))
      else next.add(key(item))
      return next
    })
  }

  function settle(kind: 'invoice' | 'arrangement') {
    const trimmed = note.trim()
    create.mutate({
      memberId: group.memberId,
      kind,
      payoutIds: checked.filter((i) => i.kind === 'payout').map((i) => i.id),
      bonusIds: checked.filter((i) => i.kind === 'bonus').map((i) => i.id),
      note: kind === 'arrangement' && trimmed !== '' ? trimmed : null,
    })
  }

  return (
    <section className="ugc-card" aria-label={`À payer à ${group.memberName}`}>
      <header className="ugc-card-head">
        <h3 className="ugc-card-title">{group.memberName}</h3>
        <span className="ugc-muted">{group.canInvoice ? 'Facture' : 'Sans statut pour facturer'}</span>
      </header>
      <ul className="ugc-checks">
        {group.items.map((item) => (
          <li key={key(item)}>
            <label className="ugc-check">
              <input type="checkbox" checked={!unchecked.has(key(item))} onChange={() => toggle(item)} />
              <span className="ugc-check-label">{item.label}</span>
              <span className="ugc-amount">{formatEuros(item.amountCents)}</span>
            </label>
          </li>
        ))}
      </ul>
      <p className="ugc-total">
        Total : <strong>{formatEuros(total)}</strong>
      </p>
      {group.canInvoice && (
        <div className="ugc-actions">
          <button
            type="button"
            className="ugc-btn ugc-btn--strong"
            disabled={create.isPending || checked.length === 0}
            onClick={() => settle('invoice')}
          >
            Facture reçue
          </button>
        </div>
      )}
      {!group.canInvoice && !arranging && (
        <div className="ugc-actions">
          <button type="button" className="ugc-btn ugc-btn--strong" disabled={checked.length === 0} onClick={() => setArranging(true)}>
            Réglé autrement
          </button>
        </div>
      )}
      {!group.canInvoice && arranging && (
        <div className="ugc-inline-form">
          <label htmlFor={noteId}>Note interne (facultative)</label>
          <textarea
            id={noteId}
            className="ugc-textarea"
            rows={2}
            maxLength={500}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <p className="ugc-muted">Pas d'IBAN ici : seulement ce qui a été convenu, par exemple « 6 mois de Pro ».</p>
          <div className="ugc-actions">
            <button
              type="button"
              className="ugc-btn ugc-btn--strong"
              disabled={create.isPending || checked.length === 0}
              onClick={() => settle('arrangement')}
            >
              Confirmer le règlement
            </button>
            <button type="button" className="ugc-btn" disabled={create.isPending} onClick={() => setArranging(false)}>
              Annuler
            </button>
          </div>
        </div>
      )}
      <ErrorLine error={create.error} />
    </section>
  )
}

// ── Règlements en cours et historique ────────────────────────────────────────

type OpenSettlement = AdminPaymentsView['open'][number]

function OpenSettlementCard({ settlement }: { settlement: OpenSettlement }) {
  const titleId = useId()
  const dateId = useId()
  const paid = useMarkSettlementPaid()
  const cancel = useCancelSettlement()
  const [paidOn, setPaidOn] = useState(parisToday)
  const [cancelling, setCancelling] = useState(false)
  const busy = paid.isPending || cancel.isPending

  return (
    <article className="ugc-card" aria-labelledby={titleId}>
      <header className="ugc-card-head">
        <h3 id={titleId} className="ugc-card-title">
          {settlement.memberName || 'Personne effacée'}
        </h3>
        <span className="ugc-muted">
          {settlement.invoiceReceivedAt ? `Facture reçue le ${formatDate(settlement.invoiceReceivedAt)}` : 'Facture reçue'}
        </span>
      </header>
      <p className="ugc-total">
        Total : <strong>{formatEuros(settlement.totalCents)}</strong>
      </p>
      {!cancelling && (
        <div className="ugc-inline-form">
          <label htmlFor={dateId}>Date du paiement</label>
          <input
            id={dateId}
            className="ugc-input"
            type="date"
            max={parisToday()}
            value={paidOn}
            onChange={(e) => setPaidOn(e.target.value)}
          />
          <div className="ugc-actions">
            <button
              type="button"
              className="ugc-btn ugc-btn--go"
              disabled={busy || !/^\d{4}-\d{2}-\d{2}$/.test(paidOn)}
              onClick={() => {
                cancel.reset()
                paid.mutate({ id: settlement.id, paidOn })
              }}
            >
              Payé
            </button>
            <button type="button" className="ugc-btn" disabled={busy} onClick={() => setCancelling(true)}>
              Annuler le règlement
            </button>
          </div>
        </div>
      )}
      {cancelling && (
        <div className="ugc-inline-form">
          <p className="ugc-confirm">Ses éléments redeviendront à payer.</p>
          <div className="ugc-actions">
            <button
              type="button"
              className="ugc-btn ugc-btn--strong"
              disabled={busy}
              onClick={() => {
                paid.reset()
                cancel.mutate(settlement.id)
              }}
            >
              Confirmer l'annulation
            </button>
            <button type="button" className="ugc-btn" disabled={busy} onClick={() => setCancelling(false)}>
              Garder le règlement
            </button>
          </div>
        </div>
      )}
      <ErrorLine error={paid.error ?? cancel.error} />
    </article>
  )
}

type PastSettlement = AdminPaymentsView['history'][number]

function HistoryRow({ settlement }: { settlement: PastSettlement }) {
  const cancel = useCancelSettlement()
  const [confirming, setConfirming] = useState(false)
  const date = settlement.paidAt ?? settlement.createdAt
  return (
    <li className="ugc-history-row">
      <span>{formatDate(date)}</span>
      <span>{settlement.memberName ?? 'Personne effacée'}</span>
      <span>{settlement.kind === 'invoice' ? 'Facture payée' : 'Réglé autrement'}</span>
      <strong>{formatEuros(settlement.totalCents)}</strong>
      {settlement.note && <span className="ugc-muted">{settlement.note}</span>}
      {/* Personne effacée : le règlement reste pour le budget, il ne s'annule plus. */}
      {!confirming && settlement.memberName !== null && (
        <button type="button" className="ugc-btn" onClick={() => setConfirming(true)}>
          Annuler
        </button>
      )}
      {confirming && (
        <span className="ugc-actions">
          <button type="button" className="ugc-btn ugc-btn--strong" disabled={cancel.isPending} onClick={() => cancel.mutate(settlement.id)}>
            Confirmer l'annulation
          </button>
          <button type="button" className="ugc-btn" disabled={cancel.isPending} onClick={() => setConfirming(false)}>
            Garder
          </button>
        </span>
      )}
      <ErrorLine error={cancel.error} />
    </li>
  )
}

// ── Onglet ───────────────────────────────────────────────────────────────────

function Section({ title, count, children }: { title: string; count?: number | undefined; children: React.ReactNode }) {
  const id = useId()
  return (
    <section className="ugc-section" aria-labelledby={id}>
      <h2 id={id} className="ugc-section-title">
        {title}
        {count !== undefined && count > 0 && <span className="ugc-muted"> ({count})</span>}
      </h2>
      {children}
    </section>
  )
}

function Loading({ query, what }: { query: { isPending: boolean; isError: boolean; error: Error | null }; what: string }) {
  if (query.isPending) return <p className="ugc-muted">Chargement…</p>
  if (query.isError) return <p className="ugc-error">{`${what} n'ont pas pu être chargés : ${query.error?.message ?? ''}`}</p>
  return null
}

export function PaymentsTab() {
  const toValidate = useUgcPublications('to_validate')
  const upcoming = useUgcPublications('upcoming')
  const all = useUgcPublications('all')
  const payments = useUgcPayments()
  const correctable = (all.data ?? []).filter(
    (item) => item.publication.status === 'reading_validated' && item.payout !== null && item.payout.settlementId === null,
  )

  return (
    <div className="ugc-tab-panel ugc-payments">
      <Section title="Captures à valider" count={toValidate.data?.length}>
        <Loading query={toValidate} what="Les captures" />
        {toValidate.data?.length === 0 && <p className="ugc-empty">Aucune capture à valider.</p>}
        <div className="ugc-cards">
          {toValidate.data?.map((item) => <ScreenshotCard key={item.publication.id} item={item} />)}
        </div>
      </Section>

      <Section title="Relevés à venir" count={upcoming.data?.filter((i) => i.missing).length}>
        <Loading query={upcoming} what="Les relevés" />
        {upcoming.data?.length === 0 && <p className="ugc-empty">Aucune publication en attente de relevé.</p>}
        <div className="ugc-cards">
          {upcoming.data?.map((item) => <UpcomingCard key={item.publication.id} item={item} />)}
        </div>
      </Section>

      <Section title="À payer" count={payments.data?.due.length}>
        <Loading query={payments} what="Les paiements" />
        {payments.data?.due.length === 0 && <p className="ugc-empty">Rien à payer.</p>}
        <div className="ugc-cards">
          {payments.data?.due.map((group) => <DueGroupCard key={group.memberId} group={group} />)}
        </div>
      </Section>

      <Section title="Règlements en cours" count={payments.data?.open.length}>
        {payments.data?.open.length === 0 && <p className="ugc-empty">Aucune facture en attente de paiement.</p>}
        <div className="ugc-cards">
          {payments.data?.open.map((s) => <OpenSettlementCard key={s.id} settlement={s} />)}
        </div>
      </Section>

      <Section title="Relevés validés non réglés">
        <Loading query={all} what="Les relevés validés" />
        {all.data && correctable.length === 0 && <p className="ugc-empty">Aucun relevé à corriger.</p>}
        <div className="ugc-cards">
          {correctable.map((item) => <ValidatedCard key={item.publication.id} item={item} />)}
        </div>
      </Section>

      <Section title="Historique des règlements">
        {payments.data?.history.length === 0 && <p className="ugc-empty">Aucun règlement pour l'instant.</p>}
        {payments.data && payments.data.history.length > 0 && (
          <ul className="ugc-history-list">
            {payments.data.history.map((s) => <HistoryRow key={s.id} settlement={s} />)}
          </ul>
        )}
      </Section>
    </div>
  )
}
